import { useCallback, useMemo } from "react";
import { IoKeyOutline, IoPersonOutline, IoRepeat } from "react-icons/io5";
import { LuAlarmClock } from "react-icons/lu";
import { MdModeEdit } from "react-icons/md";
import { useChatContext } from "../../context/ChatContext";
import { useInfoUser } from "../../store/UserStore";
import {
  extractDate,
  extractDay,
  extractMonth,
  formatTime,
} from "../../utils/formatTime";
import { DropdownSchedule } from "./DropdownSchedule";
import "./style.scss";

const ScheduleMessage = ({ message }) => {
  const { user, languageMap } = useInfoUser();
  const {
    setScheduleMessage,
    setIsChatScheduleModal,
    userListRef,
    selectedConversation,
  } = useChatContext();

  const repeatName = useMemo(() => {
    switch (message?.repeatType) {
      case "ONE_TIME":
        return languageMap?.["modal.scheduleModal.oneTime"];
      case "DAY":
        return languageMap?.["modal.scheduleModal.everyDay"];
      case "WEEK":
        return languageMap?.["modal.scheduleModal.everyWeek"];
      case "MONTH":
        return languageMap?.["modal.scheduleModal.everyMonth"];
      default:
        return languageMap?.["modal.scheduleModal.oneTime"];
    }
  }, [message?.repeatType, languageMap]);

  const generateUserState = useMemo(() => {
    const nonAcceptList = message?.nonAcceptList?.length || 0;

    if (message?.memberCount === nonAcceptList) {
      return (
        nonAcceptList + " " + languageMap?.["message.ScheduleMessage.decline"]
      );
    }

    if (nonAcceptList === 0) {
      return (
        message?.memberCount +
        " " +
        languageMap?.["message.ScheduleMessage.going"]
      );
    }

    const memberGoing = message?.memberCount - nonAcceptList;

    return [
      memberGoing + " " + languageMap?.["message.ScheduleMessage.going"],
      nonAcceptList + " " + languageMap?.["message.ScheduleMessage.decline"],
    ].join(", ");
  }, [message?.nonAcceptList, message?.memberCount, languageMap]);

  const languageType = useMemo(() => {
    switch (user?.language) {
      case "vn":
        return "vi";
      case "kr":
        return "ko";
      case "us":
        return "en-US";
      default:
        return "en-US";
    }
  }, [user?.language]);

  return (
    <div className="message-schedule flex flex-col items-center py-[5px]">
      <div className="flex flex-col items-center p-[10px] w-[400px] bg-white">
        <div className="flex gap-[20px] w-[100%]">
          <div className="w-[90px] text-center border-solid border-[1px] rounded-sm">
            <div className="flex flex-col justify-center bg-[#2a56b9] text-white h-[30px] rounded-t-sm font-semibold">
              {extractDay(message?.scheduleTime, languageType)}
            </div>
            <div className="h-[60px] text-[28px] bg-white flex items-center justify-center font-semibold">
              {extractDate(message?.scheduleTime, languageType)}
            </div>
            <div className="h-[30px] bg-white font-semibold flex items-center justify-center">
              {extractMonth(message?.scheduleTime, languageType)}
            </div>
          </div>
          <div className="flex-1 flex flex-col gap-[2px]">
            <div className="content">{message?.content}</div>
            <div className="flex gap-[5px] items-center">
              <IoRepeat className="text-[18px]" />
              <div className="schedule-info">{repeatName}</div>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex gap-[5px] items-center">
                <LuAlarmClock className="text-[18px]" />
                <div className="schedule-info">
                  {formatTime(message?.scheduleTime)}
                </div>
              </div>
              {message?.scheduleStatus === "ACTIVE" ? (
                (message?.senderId === user?.userId ||
                  selectedConversation?.role === "HOST" ||
                  selectedConversation?.role === "ADMIN" ||
                  selectedConversation?.type === "PERSONAL") && (
                  <button
                    onClick={() => {
                      setScheduleMessage(message);
                      setIsChatScheduleModal(true);
                    }}
                  >
                    <MdModeEdit className="text-[18px]" />
                  </button>
                )
              ) : (
                <div className="text-[12px] text-[white] bg-[red] px-[4px] rounded-[3px]">
                  closed
                </div>
              )}
            </div>
            <div className="flex gap-[5px] items-center">
              <IoKeyOutline className="text-[18px]" />
              <div className="schedule-info">
                {userListRef.current?.find((u) => u?.userId === message?.senderId)?.name}
              </div>
            </div>
            <div className="flex gap-[5px] items-center">
              <IoPersonOutline className="text-[18px]" />
              <div className="schedule-info">{generateUserState}</div>
            </div>
          </div>
        </div>
        <div className="bg-[#ebecf0] rounded-sm flex justify-between w-[100%] mt-[10px] p-[8px]">
          {message?.nonAcceptList?.includes(user?.userId) ? (
            <div className="font-semibold">
              {languageMap?.["message.ScheduleMessage.youConfirmedToReject"]}
            </div>
          ) : (
            <div className="font-semibold">
              {languageMap?.["message.ScheduleMessage.youConfirmedToJoin"]}
            </div>
          )}
          {message?.scheduleStatus === "ACTIVE" && (
            <span className="action-button">
              <DropdownSchedule message={message} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export { ScheduleMessage };
